import { useEffect, useState, memo } from "react";

import { Box, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { PRIVATE_PAGES } from "@app/router/pages";
import { useAppDispatch, useAppSelector } from "@app/store";
import {
  setCertificates,
  useDeleteCertificateMutation,
  useLazyGetCertificatesQuery,
  useReorderCertificatesMutation
} from "@modules/certificate";
import Table from '@mui/material/Table';
import { FilterParams } from "types";
import { PARAMS } from "@constants/index";
import { ICertificate } from "@modules/certificate/types";
import { useLazyGetCategoriesQuery } from "@modules/category";
import { ICategory } from "@modules/category/types";

import { StyledBody, TitleWrapper, EachWrapper, Actions, Wrapper } from "./styled";

const Certificates = () => {
  const certificateList = useAppSelector((state) => state.certificate.certificates);

  const [state, setState] = useState<ICertificate[]>([]);
  const dispatch = useAppDispatch();
  const [getCertificate] = useLazyGetCertificatesQuery()
  const [deleteCertificate] = useDeleteCertificateMutation()
  const [updateSorting] = useReorderCertificatesMutation()
  const [getCategories] = useLazyGetCategoriesQuery()

  const [categories, setCategories] = useState([] as ICategory[])
  const [pagesCount, setPagesCount] = useState<number>(1);
  const [allow, setAllow] = useState(false);
  const [filters, setFilters] = useState<FilterParams>({
    limit: PARAMS.LIMIT,
    offset: PARAMS.OFFSET,
  })

  const handleGetRequest = async () => {
    try {
      const [
        certificateResponse,
        categoriesResponse
      ] = await Promise.all([
        await getCertificate(filters),
        await getCategories({ offset: 0, limit: 500 })
      ])

      if (certificateResponse.data) {
        dispatch(setCertificates(certificateResponse.data.certificates))
        setPagesCount(certificateResponse.data.count || 1)
      }

      if (categoriesResponse.data) {
        setCategories(categoriesResponse.data.certificateCategory)
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteCertificate = async (id?: string) => {
    if (!id) {
      return null
    }

    try {
      await deleteCertificate({ id })
      handleGetRequest()
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    handleGetRequest()
  }, [filters])

  useEffect(() => {
    if (allow && state?.length) {
      const prepareAndRequest = async () => {
        const ordered = state?.map((item, index) => ({
          sort: index + 1,
        }))

        await updateSorting(ordered)
        setAllow(false)
      }

      prepareAndRequest()
    }
  }, [state, allow])

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '20px',
        flexDirection: 'column',
        alignItems: 'flex-start'
      }}
    >
      <Link to={PRIVATE_PAGES.CREATE_CERTIFICATE}>
        <Button variant="contained" color="primary">
          Create Certificate
        </Button>
      </Link>

      <Wrapper>
        <Table>
          <TitleWrapper>
            <div>Image</div>
            <div>PDF</div>
            <div>Categories</div>
            <div>TITLE AM</div>
            <div>TITLE En</div>
            <div>TITLE Ru</div>
            <div>ACTIONS</div>
          </TitleWrapper>
          <StyledBody
            list={state}
            setList={setState}
            onEnd={() => {
              setAllow(true)
            }}
          >
            {certificateList?.map((certificate) => {
              const images = certificate?.images
              const pdfIndex = images.findIndex((item) => item.url.split('.').pop() === 'pdf')

              return (
                <EachWrapper key={certificate.id}>
                  <div>
                    <img
                      width="80px"
                      height="80px"
                      src={`${process.env.REACT_APP_API_BASE_URL}/${images[pdfIndex === 0 ? 1 : 0]?.url}`}
                      alt={certificate.titleAm}
                    />
                  </div>
                  <div>
                    {images[pdfIndex] && (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`${process.env.REACT_APP_API_BASE_URL}/${images[pdfIndex]?.url}`}
                      >
                        View PDF
                      </a>
                    )}
                  </div>
                  <div>
                    {
                      certificate.categoryIds && categories.length && (
                        <span>
                          {
                            categories.map((item) => {
                              if (certificate.categoryIds.includes(item.id)) {
                                return <p>{item.titleAm}</p>
                              }

                              return null
                            })
                          }
                        </span>
                      )
                    }
                  </div>
                  <div>{certificate.titleAm}</div>
                  <div>{certificate.titleEn}</div>
                  <div>{certificate.titleRu}</div>
                  <Actions>
                    <Link to={`/update-certificate/${certificate?.id}`}>
                      <Button
                        type="button"
                        variant="outlined"
                      >
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="contained"
                      type="button"
                      onClick={() => handleDeleteCertificate(certificate?.id)}
                    >
                      Delete
                    </Button>
                  </Actions>
                </EachWrapper>
              )
            })}
          </StyledBody>
        </Table>
      </Wrapper>
    </Box>
  )
};

export default memo(Certificates);
